/**
 * Three.js scene setup and 3D visualization
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { Mat3, Vec3 } from '../sim/linalg';

export class SimulationScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;

  // Visualization objects
  private topMesh: THREE.Mesh;
  private inertiaEllipsoid: THREE.Mesh;
  private bodyAxes: THREE.Group;
  private worldAxes: THREE.Group;
  private angularMomentumArrow: THREE.ArrowHelper;
  private trailPoints: THREE.Vector3[] = [];
  private trailLine: THREE.Line;
  private groundPlane!: THREE.Mesh;
  private gridHelper!: THREE.GridHelper;

  // Configuration
  private showAngularMomentum = true;
  private showTrail = true;
  private trailLength = 200;

  constructor(canvas: HTMLCanvasElement) {
    // Scene setup
    this.scene = new THREE.Scene();
    this.updateBackground();

    // Add theme change listener
    window.addEventListener('theme-change', () => this.updateBackground());

    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      50,
      canvas.clientWidth / canvas.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(5, 5, 5);
    this.camera.lookAt(0, 0, 0);

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false
    });
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Orbit controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 20;

    // Lighting
    this.setupLighting();

    // Create visualization objects
    this.topMesh = this.createTopMesh();
    this.inertiaEllipsoid = this.createInertiaEllipsoid();
    this.bodyAxes = this.createAxes(1.5, true);
    this.worldAxes = this.createAxes(2, false);
    this.angularMomentumArrow = this.createAngularMomentumArrow();
    this.trailLine = this.createTrailLine();

    // Add to scene
    this.scene.add(this.topMesh);
    this.scene.add(this.inertiaEllipsoid);
    this.scene.add(this.bodyAxes);
    this.scene.add(this.worldAxes);
    this.scene.add(this.angularMomentumArrow);
    this.scene.add(this.trailLine);

    // Add ground plane
    this.addGroundPlane();

    // Update background again after ground plane is created
    this.updateBackground();

    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());
  }

  private setupLighting(): void {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(ambientLight);

    // Main directional light
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.8);
    mainLight.position.set(5, 10, 5);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 50;
    this.scene.add(mainLight);

    // Fill light
    const fillLight = new THREE.DirectionalLight(0x4477ff, 0.3);
    fillLight.position.set(-5, 3, -5);
    this.scene.add(fillLight);

    // Rim light
    const rimLight = new THREE.DirectionalLight(0xff8844, 0.2);
    rimLight.position.set(0, 5, -5);
    this.scene.add(rimLight);
  }

  private createTopMesh(): THREE.Mesh {
    // Create a spinning top shape using lathe geometry
    const points: THREE.Vector2[] = [];
    points.push(new THREE.Vector2(0, 0));
    points.push(new THREE.Vector2(0.1, 0.2));
    points.push(new THREE.Vector2(0.3, 0.4));
    points.push(new THREE.Vector2(0.4, 0.6));
    points.push(new THREE.Vector2(0.3, 0.8));
    points.push(new THREE.Vector2(0.2, 1.0));
    points.push(new THREE.Vector2(0, 1.2));

    const geometry = new THREE.LatheGeometry(points, 32);
    const material = new THREE.MeshStandardMaterial({
      color: 0x00ff88,
      metalness: 0.6,
      roughness: 0.3,
      emissive: 0x002211,
      emissiveIntensity: 0.2
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.visible = false; // Start hidden, show ellipsoid by default
    return mesh;
  }

  private createInertiaEllipsoid(): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(1, 32, 32);
    const material = new THREE.MeshPhysicalMaterial({
      color: 0x66aaff,
      metalness: 0.3,
      roughness: 0.2,
      transparent: true,
      opacity: 0.8,
      emissive: 0x2266aa,
      emissiveIntensity: 0.3,
      clearcoat: 1.0,
      clearcoatRoughness: 0.1
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  private createAxes(length: number, _isBodyAxes: boolean): THREE.Group {
    const group = new THREE.Group();

    const colors = [0xff0000, 0x00ff00, 0x0088ff]; // X, Y, Z

    for (let i = 0; i < 3; i++) {
      const material = new THREE.LineBasicMaterial({ color: colors[i], linewidth: 2 });
      const points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0)];
      points[1].setComponent(i, length);

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, material);
      group.add(line);

      // Add arrow head
      const arrowGeometry = new THREE.ConeGeometry(0.05, 0.15, 8);
      const arrowMaterial = new THREE.MeshBasicMaterial({ color: colors[i] });
      const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
      arrow.position.setComponent(i, length);

      // Orient arrow
      if (i === 0) arrow.rotation.z = -Math.PI / 2;
      else if (i === 2) arrow.rotation.x = Math.PI / 2;

      group.add(arrow);
    }

    return group;
  }

  private createAngularMomentumArrow(): THREE.ArrowHelper {
    const dir = new THREE.Vector3(0, 1, 0);
    const origin = new THREE.Vector3(0, 0, 0);
    const length = 2;
    const color = 0xffff00;

    return new THREE.ArrowHelper(dir, origin, length, color, 0.3, 0.2);
  }

  private createTrailLine(): THREE.Line {
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.6
    });

    return new THREE.Line(geometry, material);
  }

  private addGroundPlane(): void {
    const geometry = new THREE.PlaneGeometry(20, 20);
    const material = new THREE.MeshStandardMaterial({
      color: 0x2a2a2a,
      roughness: 0.8,
      metalness: 0.2
    });

    this.groundPlane = new THREE.Mesh(geometry, material);
    this.groundPlane.rotation.x = -Math.PI / 2;
    this.groundPlane.position.y = -2;
    this.groundPlane.receiveShadow = true;
    this.scene.add(this.groundPlane);

    // Add grid
    this.gridHelper = new THREE.GridHelper(20, 20, 0x444444, 0x333333);
    this.gridHelper.position.y = -1.99;
    this.scene.add(this.gridHelper);
  }

  /**
   * Update scene with new simulation state
   */
  update(R: Mat3, _omega: Vec3, L?: Vec3, inertia?: { I1: number; I2: number; I3: number }): void {
    // Update rotation matrix for body axes and mesh
    const matrix = new THREE.Matrix4();
    matrix.set(
      R[0], R[1], R[2], 0,
      R[3], R[4], R[5], 0,
      R[6], R[7], R[8], 0,
      0, 0, 0, 1
    );

    this.bodyAxes.rotation.setFromRotationMatrix(matrix);
    this.topMesh.rotation.setFromRotationMatrix(matrix);
    this.inertiaEllipsoid.rotation.setFromRotationMatrix(matrix);

    // Update inertia ellipsoid scale
    if (inertia) {
      const scale = 0.5; // Overall scaling factor
      this.inertiaEllipsoid.scale.set(
        scale * Math.sqrt(inertia.I1),
        scale * Math.sqrt(inertia.I2),
        scale * Math.sqrt(inertia.I3)
      );
    }

    // Update angular momentum arrow
    if (L && this.showAngularMomentum) {
      const LVec = new THREE.Vector3(L[0], L[1], L[2]);
      const LMag = LVec.length();

      if (LMag > 0.01) {
        this.angularMomentumArrow.setDirection(LVec.normalize());
        this.angularMomentumArrow.setLength(Math.min(LMag * 0.2, 3), 0.3, 0.2);
        this.angularMomentumArrow.visible = true;
      } else {
        this.angularMomentumArrow.visible = false;
      }
    }

    // Update trail
    if (this.showTrail) {
      // Use the z-axis of the body frame as trail point
      const trailPoint = new THREE.Vector3(R[2], R[5], R[8]);
      this.trailPoints.push(trailPoint);

      // Limit trail length
      if (this.trailPoints.length > this.trailLength) {
        this.trailPoints.shift();
      }

      // Update trail geometry
      const geometry = new THREE.BufferGeometry().setFromPoints(this.trailPoints);
      this.trailLine.geometry.dispose();
      this.trailLine.geometry = geometry;
    }
  }

  /**
   * Update inertia ellipsoid shape based on principal moments
   */
  updateInertia(inertia: { I1: number; I2: number; I3: number }): void {
    const scale = 0.5; // Overall scaling factor
    this.inertiaEllipsoid.scale.set(
      scale * Math.sqrt(inertia.I1),
      scale * Math.sqrt(inertia.I2),
      scale * Math.sqrt(inertia.I3)
    );
  }

  /**
   * Update scene background and ground plane colors
   */
  updateBackground(): void {
    // Get colors from CSS variables
    const style = getComputedStyle(document.documentElement);
    const bgColor = style.getPropertyValue('--canvas-bg').trim();
    const theme = document.documentElement.getAttribute('data-theme');
    const isLight = theme === 'light';

    const color = new THREE.Color(bgColor);
    this.scene.background = color;
    this.scene.fog = new THREE.Fog(color.getHex(), 10, 50);

    // Update ground plane for theme
    if (this.groundPlane && this.groundPlane.material instanceof THREE.MeshStandardMaterial) {
      this.groundPlane.material.color.setHex(isLight ? 0xe0e0e0 : 0x2a2a2a);
    }

    // Update grid colors
    if (this.gridHelper) {
      this.gridHelper.material.color.setHex(isLight ? 0xb0b0b0 : 0x444444);
      // @ts-ignore - GridHelper has a second color property
      if (this.gridHelper.material.color2) {
        // @ts-ignore
        this.gridHelper.material.color2.setHex(isLight ? 0xd0d0d0 : 0x333333);
      }
    }
  }

  render(): void {
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Handle window resize
   */
  private onWindowResize(): void {
    const canvas = this.renderer.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  /**
   * Clear trail
   */
  clearTrail(): void {
    this.trailPoints = [];
    this.trailLine.geometry.dispose();
    this.trailLine.geometry = new THREE.BufferGeometry();
  }

  /**
   * Toggle visualization options
   */
  setShowEllipsoid(show: boolean): void {
    this.inertiaEllipsoid.visible = show;
  }

  setShowTopMesh(show: boolean): void {
    this.topMesh.visible = show;
  }

  setShowBodyAxes(show: boolean): void {
    this.bodyAxes.visible = show;
  }

  setShowWorldAxes(show: boolean): void {
    this.worldAxes.visible = show;
  }

  setShowAngularMomentum(show: boolean): void {
    this.showAngularMomentum = show;
    this.angularMomentumArrow.visible = show;
  }

  setShowTrail(show: boolean): void {
    this.showTrail = show;
    this.trailLine.visible = show;
  }

  setTrailLength(length: number): void {
    this.trailLength = length;
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    this.renderer.dispose();
    this.controls.dispose();

    // Dispose geometries and materials
    this.topMesh.geometry.dispose();
    (this.topMesh.material as THREE.Material).dispose();
    this.inertiaEllipsoid.geometry.dispose();
    (this.inertiaEllipsoid.material as THREE.Material).dispose();
    this.trailLine.geometry.dispose();
    (this.trailLine.material as THREE.Material).dispose();
  }
}
